import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "partialOptions",
    "firstDurationSelect",
    "lastDurationSelect",
    "isPartialFirst",
    "isPartialLast",
    "firstDayFields",
    "lastDayFields",
    "firstHalfHint",
    "lastHalfHint",
    "lastDaySection",
    "leaveType",
    "hoursFirstDayInput",
    "hoursLastDayInput",
    "calculatedHoursFirst",
    "calculatedHoursLast",
    "timeFromFirstDay",
    "timeToFirstDay",
    "timeFromLastDay",
    "timeToLastDay",
    "timeErrorFirst",
    "timeErrorLast",
    "balancePreview",
  ]
  static values = {
    allowsPartial: Boolean,
    allowsHalf: Boolean,
    timeErrorMessage: { type: String, default: "End time must be after start time" },
    balancePreviewUrl: String,
    // The employee the form is fixed to, where it has no select to read one from
    // (see #selectedEmployeeId), and the id of the request being edited.
    employeeId: String,
    leaveRequestId: String,
  }

  connect() {
    this.buildLeaveTypeMap()
    this.syncSelectedType()
    this.applyTypePermissions()
    this.updateVisibility()
    this.updateLastDayVisibility()
    this.watchEmployeeSelect()
    this.refreshBalancePreview()
  }

  disconnect() {
    clearTimeout(this.balancePreviewTimeout)
    if (this.employeeSelect && this.employeeChanged) {
      this.employeeSelect.removeEventListener("change", this.employeeChanged)
    }
  }

  // The employee select carries no data-action of its own (it is shared with the
  // bulk-select and SlimSelect wiring), so the preview subscribes to it here.
  //
  // `select` first, deliberately: Rails puts a hidden blank input UNDER THE SAME
  // NAME in front of a multiple select, so a bare [name*="employee_id"] matches
  // that input — which never changes and always reads "" — and the preview would
  // stay empty however many employees are picked.
  watchEmployeeSelect() {
    this.employeeSelect =
      this.element.querySelector('select[name*="employee_id"]') ||
      this.element.querySelector('[name*="employee_id"]:not([type="hidden"])')
    if (!this.employeeSelect) return

    this.employeeChanged = () => this.refreshBalancePreview()
    this.employeeSelect.addEventListener("change", this.employeeChanged)
  }

  buildLeaveTypeMap() {
    // Map leave type IDs to their allows_partial_days / allows_half_days settings
    this.leaveTypeMap = {}
    const dataElements = this.element.querySelectorAll("[data-leave-type-id]")
    dataElements.forEach((el) => {
      this.leaveTypeMap[el.dataset.leaveTypeId] = {
        partial: el.dataset.allowsPartial === "true",
        half: el.dataset.allowsHalf === "true",
      }
    })
  }

  // Read the currently selected type's permissions into the controller values.
  syncSelectedType() {
    const select = this.element.querySelector('[name*="leave_type_id"]')
    const selectedId = select ? select.value : null
    const perms = (this.leaveTypeMap && this.leaveTypeMap[selectedId]) || {}
    this.allowsPartialValue = perms.partial === true
    this.allowsHalfValue = perms.half === true
  }

  leaveTypeChanged() {
    this.syncSelectedType()
    this.applyTypePermissions()
    this.updateVisibility()
    this.refreshBalancePreview()
  }

  dateChanged() {
    this.updateLastDayVisibility()
    this.refreshBalancePreview()
  }

  // Debounced: every keystroke in a date field and every duration switch would
  // otherwise be its own request while the form is still being filled in.
  refreshBalancePreview() {
    if (!this.hasBalancePreviewTarget || !this.balancePreviewUrlValue) return

    clearTimeout(this.balancePreviewTimeout)
    this.balancePreviewTimeout = setTimeout(() => this.loadBalancePreview(), 300)
  }

  // Points the preview frame at the endpoint; Turbo does the rest. With several
  // employees selected the preview follows the FIRST one — one figure, for the
  // employee whose row the admin is looking at.
  loadBalancePreview() {
    const frame = this.balancePreviewTarget
    const employeeId = this.selectedEmployeeId()
    const dateFrom = this.fieldValue("date_from")
    const dateTo = this.fieldValue("date_to")

    if (!employeeId || !dateFrom || !dateTo) {
      frame.removeAttribute("src")
      frame.innerHTML = ""
      return
    }

    const params = new URLSearchParams({
      employee_id: employeeId,
      date_from: dateFrom,
      date_to: dateTo,
    })
    const leaveTypeId = this.fieldValue("leave_type_id")
    if (leaveTypeId) params.set("leave_type_id", leaveTypeId)
    // The admin form's legacy select, where the tenant has no active leave types.
    const typeOfLeave = this.fieldValue("type_of_leave")
    if (typeOfLeave) params.set("type_of_leave", typeOfLeave)
    // Editing: the endpoint measures against the account WITHOUT this request,
    // or its own days would be charged twice (Leave::BalanceCheck#except_ids).
    if (this.leaveRequestIdValue) params.set("leave_request_id", this.leaveRequestIdValue)
    if (this.hasFirstDurationSelectTarget)
      params.set("first_day_duration_type", this.firstDurationSelectTarget.value)
    if (this.hasLastDurationSelectTarget)
      params.set("last_day_duration_type", this.lastDurationSelectTarget.value)
    if (this.hasIsPartialFirstTarget)
      params.set("is_partial_first_day", this.isPartialFirstTarget.value)
    if (this.hasIsPartialLastTarget)
      params.set("is_partial_last_day", this.isPartialLastTarget.value)
    if (this.hasHoursFirstDayInputTarget && this.hoursFirstDayInputTarget.value)
      params.set("hours_first_day", this.hoursFirstDayInputTarget.value)
    if (this.hasHoursLastDayInputTarget && this.hoursLastDayInputTarget.value)
      params.set("hours_last_day", this.hoursLastDayInputTarget.value)

    frame.src = `${this.balancePreviewUrlValue}?${params.toString()}`
  }

  // Without a select the employee is fixed and comes from the value: an employee
  // filing their own request never sees one (the my/ form renders it for proxy
  // submitters only), and no edit form of either surface renders one either.
  selectedEmployeeId() {
    if (!this.employeeSelect) return this.employeeIdValue || null
    if (this.employeeSelect.multiple) {
      const selected = this.employeeSelect.selectedOptions
      return selected.length > 0 ? selected[0].value : null
    }
    return this.employeeSelect.value || null
  }

  fieldValue(name) {
    const field = this.element.querySelector(`[name*="${name}"]`)
    return field ? field.value : null
  }

  // Enable/disable duration options per the selected type's permissions and
  // reset any now-invalid selection back to a full day.
  applyTypePermissions() {
    ;[this.durationSelect("first"), this.durationSelect("last")]
      .filter((select) => select)
      .forEach((select) => {
        Array.from(select.options).forEach((option) => {
          const requires = option.dataset.requires
          let allowed = true
          if (requires === "partial") allowed = this.allowsPartialValue
          if (requires === "half") allowed = this.allowsHalfValue
          option.disabled = !allowed
          option.hidden = !allowed
        })
        const current = select.options[select.selectedIndex]
        if (current && (current.disabled || current.hidden)) {
          select.value = "full_day"
        }
      })
    this.firstDurationChanged()
    this.lastDurationChanged()
  }

  firstDurationChanged() {
    this.applyDuration("first")
  }

  lastDurationChanged() {
    this.applyDuration("last")
  }

  // Drive the per-day sub-sections and the hidden is_partial flag from the
  // selected duration type.
  applyDuration(slot) {
    const select = this.durationSelect(slot)
    if (!select) return

    const value = select.value
    const isHours = value === "hours"
    const isHalf = value.startsWith("half_day")
    const isPartial = value !== "full_day"

    const hiddenPartial = this.optionalTarget(slot === "first" ? "isPartialFirst" : "isPartialLast")
    if (hiddenPartial) hiddenPartial.value = isPartial ? "1" : "0"

    const fields = this.optionalTarget(slot === "first" ? "firstDayFields" : "lastDayFields")
    if (fields) fields.classList.toggle("hidden", !isHours)

    const hint = this.optionalTarget(slot === "first" ? "firstHalfHint" : "lastHalfHint")
    if (hint) hint.classList.toggle("hidden", !isHalf)

    if (!isHours) this.clearTimeInputs(slot)
    this.refreshBalancePreview()
  }

  // The duration block only exists where the tenant has configurable leave types:
  // the admin form falls back to the legacy type_of_leave select without them and
  // renders no duration selects at all. A bare `this.xTarget` throws when missing.
  durationSelect(slot) {
    return this.optionalTarget(slot === "first" ? "firstDurationSelect" : "lastDurationSelect")
  }

  optionalTarget(name) {
    return this[`has${this.capitalize(name)}Target`] ? this[`${name}Target`] : null
  }

  clearTimeInputs(slot) {
    const day = slot === "first" ? "First" : "Last"
    ;[`hours${day}DayInput`, `timeFrom${day}Day`, `timeTo${day}Day`]
      .map((name) => this.optionalTarget(name))
      .forEach((input) => input && (input.value = ""))
    ;[`calculatedHours${day}`, `timeError${day}`]
      .map((name) => this.optionalTarget(name))
      .forEach((el) => el && el.classList.add("hidden"))
  }

  capitalize(name) {
    return name.charAt(0).toUpperCase() + name.slice(1)
  }

  calculateHoursDifference(timeFrom, timeTo) {
    if (!timeFrom || !timeTo) return null

    const [fromHours, fromMinutes] = timeFrom.split(":").map(Number)
    const [toHours, toMinutes] = timeTo.split(":").map(Number)

    if (isNaN(fromHours) || isNaN(fromMinutes) || isNaN(toHours) || isNaN(toMinutes)) return null

    const fromTotal = fromHours * 60 + fromMinutes
    const toTotal = toHours * 60 + toMinutes

    if (toTotal <= fromTotal) return null // End must be after start

    const diffMinutes = toTotal - fromTotal
    return Math.round((diffMinutes / 60) * 4) / 4 // Round to nearest 0.25
  }

  updateFirstDayHours() {
    this.refreshBalancePreview()
    if (!this.hasTimeFromFirstDayTarget || !this.hasTimeToFirstDayTarget) return

    const timeFrom = this.timeFromFirstDayTarget.value
    const timeTo = this.timeToFirstDayTarget.value
    const hours = this.calculateHoursDifference(timeFrom, timeTo)

    if (hours !== null) {
      if (this.hasHoursFirstDayInputTarget) this.hoursFirstDayInputTarget.value = hours
      if (this.hasCalculatedHoursFirstTarget) {
        this.calculatedHoursFirstTarget.textContent = `${hours} h`
        this.calculatedHoursFirstTarget.classList.remove("hidden")
      }
      if (this.hasTimeErrorFirstTarget) this.timeErrorFirstTarget.classList.add("hidden")
    } else if (timeFrom && timeTo) {
      if (this.hasTimeErrorFirstTarget) {
        this.timeErrorFirstTarget.textContent = this.timeErrorMessageValue
        this.timeErrorFirstTarget.classList.remove("hidden")
      }
      if (this.hasHoursFirstDayInputTarget) this.hoursFirstDayInputTarget.value = ""
      if (this.hasCalculatedHoursFirstTarget)
        this.calculatedHoursFirstTarget.classList.add("hidden")
    } else {
      // Clear if one of the times is missing
      if (this.hasCalculatedHoursFirstTarget)
        this.calculatedHoursFirstTarget.classList.add("hidden")
      if (this.hasTimeErrorFirstTarget) this.timeErrorFirstTarget.classList.add("hidden")
    }
  }

  updateLastDayHours() {
    this.refreshBalancePreview()
    if (!this.hasTimeFromLastDayTarget || !this.hasTimeToLastDayTarget) return

    const timeFrom = this.timeFromLastDayTarget.value
    const timeTo = this.timeToLastDayTarget.value
    const hours = this.calculateHoursDifference(timeFrom, timeTo)

    if (hours !== null) {
      if (this.hasHoursLastDayInputTarget) this.hoursLastDayInputTarget.value = hours
      if (this.hasCalculatedHoursLastTarget) {
        this.calculatedHoursLastTarget.textContent = `${hours} h`
        this.calculatedHoursLastTarget.classList.remove("hidden")
      }
      if (this.hasTimeErrorLastTarget) this.timeErrorLastTarget.classList.add("hidden")
    } else if (timeFrom && timeTo) {
      if (this.hasTimeErrorLastTarget) {
        this.timeErrorLastTarget.textContent = this.timeErrorMessageValue
        this.timeErrorLastTarget.classList.remove("hidden")
      }
      if (this.hasHoursLastDayInputTarget) this.hoursLastDayInputTarget.value = ""
      if (this.hasCalculatedHoursLastTarget) this.calculatedHoursLastTarget.classList.add("hidden")
    } else {
      // Clear if one of the times is missing
      if (this.hasCalculatedHoursLastTarget) this.calculatedHoursLastTarget.classList.add("hidden")
      if (this.hasTimeErrorLastTarget) this.timeErrorLastTarget.classList.add("hidden")
    }
  }

  updateVisibility() {
    if (this.hasPartialOptionsTarget) {
      const show = this.allowsPartialValue || this.allowsHalfValue
      this.partialOptionsTarget.classList.toggle("hidden", !show)
    }
    this.updateLastDayVisibility()
  }

  updateLastDayVisibility() {
    if (!this.hasLastDaySectionTarget) return

    const dateFrom = this.element.querySelector('[name*="date_from"]')?.value
    const dateTo = this.element.querySelector('[name*="date_to"]')?.value
    const isMultiDay = dateFrom && dateTo && dateFrom !== dateTo
    this.lastDaySectionTarget.classList.toggle("hidden", !isMultiDay)

    // A single-day request has no last day — force it back to a full day.
    if (!isMultiDay && this.hasLastDurationSelectTarget) {
      this.lastDurationSelectTarget.value = "full_day"
      this.lastDurationChanged()
    }
  }
}
